import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Menu, Tabs, Tooltip } from 'antd';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import classnames from 'classnames';
import IconCard from '@web-common/components/UI/IconCard';
import { apiHolder, apis, util } from 'api';
import styles from './MailTab.module.scss';
import { actions as mailTabActions, tabId, tabType, WriteMailTypes } from '@web-common/state/reducer/mailTabReducer';
import { MailActions, useAppDispatch, useAppSelector } from '@web-common/state/createStore';
import TabMemuClose from '@web-common/components/UI/Icons/svgs/mail/TabMemuClose';
import TabCloseIcon from '@web-common/components/UI/Icons/svgs/mail/TabCloseIcon';
import WriteLetterIcon from '@web-common/components/UI/Icons/svgs/WriteLetterSvg';
import { getIn18Text } from 'api';
const trackApi = apiHolder.api.requireLogicalApi(apis.dataTrackerApiImp);
const htmlApi = apiHolder.api.requireLogicalApi(apis.htmlApi);
const { TabPane } = Tabs;
var ItemTypes;
(function (ItemTypes) {
    ItemTypes["DND_TAB"] = "DND_TAB";
})(ItemTypes || (ItemTypes = {}));
const tag = '[MailTab]';
const systemApi = apiHolder.api.getSystemApi();
const inElectron = systemApi.isElectron;
const isMac = inElectron() ? window.electronLib.env.isMac : apiHolder.env.isMac;
const TAB_FLOAT_COUNT = isMac ? 15 : 13;
const DefaultPromiseFn = () => Promise.resolve(true);
const tooltipContainer = () => (document.getElementById('mailboxModule') || document.getElementsByClassName('mailtab-layout')[0] || document.body);
const TabTitle = ({ data, activeKey, orginActiveKey }) => {
    const decodeTitle = useMemo(() => {
        return htmlApi.decodeHtml(data === null || data === void 0 ? void 0 : data.title);
    }, [data === null || data === void 0 ? void 0 : data.title]);
    const [showPopover, setShowPopover] = useState(false);
    const titleRef = useRef(null);
    const validShowPopover = useCallback(() => {
        var _a, _b;
        if (titleRef === null || titleRef === void 0 ? void 0 : titleRef.current) {
            setShowPopover(((_a = titleRef === null || titleRef === void 0 ? void 0 : titleRef.current) === null || _a === void 0 ? void 0 : _a.scrollWidth) > ((_b = titleRef === null || titleRef === void 0 ? void 0 : titleRef.current) === null || _b === void 0 ? void 0 : _b.clientWidth));
        }
    }, []);
    useEffect(() => {
        validShowPopover();
    }, [decodeTitle]);
    const title = useMemo(() => {
        var _a, _b;
        const tabIsActive = data.id === activeKey;
        let proxyTitle = '';
        if ((orginActiveKey === null || orginActiveKey === void 0 ? void 0 : orginActiveKey.includes('/')) && orginActiveKey.split('/')[0] == data.id && (data === null || data === void 0 ? void 0 : data.subTabs)) {
            const activeSubTab = (_a = data === null || data === void 0 ? void 0 : data.subTabs) === null || _a === void 0 ? void 0 : _a.find(item => (item === null || item === void 0 ? void 0 : item.id) == orginActiveKey);
            if (activeSubTab && (activeSubTab === null || activeSubTab === void 0 ? void 0 : activeSubTab.proxyTitle)) {
                proxyTitle = htmlApi.decodeHtml(activeSubTab === null || activeSubTab === void 0 ? void 0 : activeSubTab.proxyTitle);
            }
        }
        if (data.id === tabId.readMain) {
            const activeSubTab = (_b = data === null || data === void 0 ? void 0 : data.subTabs) === null || _b === void 0 ? void 0 : _b.find(item => !!item.isActive);
            if ((activeSubTab === null || activeSubTab === void 0 ? void 0 : activeSubTab.type) == tabType.subordinate || (activeSubTab === null || activeSubTab === void 0 ? void 0 : activeSubTab.type) == tabType.customer) {
                if (activeSubTab === null || activeSubTab === void 0 ? void 0 : activeSubTab.proxyTitle) {
                    proxyTitle = htmlApi.decodeHtml(activeSubTab === null || activeSubTab === void 0 ? void 0 : activeSubTab.proxyTitle);
                }
            }
            else {
                proxyTitle = htmlApi.decodeHtml(data.title);
            }
        }
        return (React.createElement("span", { className: classnames(styles.tabPane, { [styles.tabPaneActive]: tabIsActive }) },
            WriteMailTypes.includes(data.type) && (React.createElement("span", { className: styles.tabIcon },
                React.createElement(WriteLetterIcon, { opacity: data.id === activeKey || !systemApi.isElectron() ? '1' : '0.4', stroke: data.id === activeKey || !systemApi.isElectron() ? '#262A33' : '#FFFFFF' }))),
            (data === null || data === void 0 ? void 0 : data.render) ? (data.render(data, tabIsActive)) : (React.createElement("span", { className: styles.tabTitle, ref: titleRef }, proxyTitle ? proxyTitle : decodeTitle))));
    }, [data, activeKey, orginActiveKey]);
    return (React.createElement(Tooltip, { placement: "bottom", getPopupContainer: tooltipContainer, overlayClassName: classnames(styles.tabNodeTip, { [styles.tabNodeTipHide]: !showPopover }), autoAdjustOverflow: true, title: React.createElement("div", { className: styles.tabNodeTipCnt }, util.chopStrToByteSize(decodeTitle, 60)) }, title));
};
const trackMailTabClick = (id) => {
    if (id == tabId.readCustomer) {
        trackApi.track('wiamao_mail_tab', { type: 'The_customer_mail' });
    }
    else if (id == tabId.subordinate) {
        trackApi.track('wiamao_mail_tab', { type: 'Subordinate_mail' });
    }
};
const MailTab = props => {
    const { onBeforeClose = DefaultPromiseFn, onDbClick = DefaultPromiseFn } = props;
    const [activeKey, setActiveKey] = useState('');
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [isDropdownButtonShow, setDropdownButtonShow] = useState(true);
    const [isDropdownButtonFloat, setDropdownButtonFloat] = useState(false);
    const currentTabId = useAppSelector(state => state.mailTabReducer.currentTab.id);
    const tabList = useAppSelector(state => state.mailTabReducer.tabList);
    const readTabList = useAppSelector(state => state.mailTabReducer.readTabList);
    const aiAssistSideBarVisiable = useAppSelector(state => state.mailReducer.aiAssistSideBarVisiable);
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (tabList.length >= TAB_FLOAT_COUNT && !isDropdownButtonShow) {
            setDropdownButtonFloat(true);
        }
        else if (tabList.length < TAB_FLOAT_COUNT) {
            setDropdownButtonFloat(false);
        }
    }, [tabList.length, isDropdownButtonShow]);
    const setTabActive = useCallback((_activeKey) => {
        setActiveKey(key => {
            if (_activeKey !== key) {
                if (_activeKey === null || _activeKey === void 0 ? void 0 : _activeKey.includes('/')) {
                    return _activeKey === null || _activeKey === void 0 ? void 0 : _activeKey.split('/')[0];
                }
                return _activeKey;
            }
            return key;
        });
    }, []);
    const onSwitch = useCallback((e, id) => {
        console.log(tag, 'onSwitch', id);
        setDropdownVisible(false);
        setTabActive(id);
        dispatch(mailTabActions.doChangeCurrentTab(id));
    }, []);
    const onTabDbClick = useCallback((e, id) => {
        e.preventDefault();
        e.stopPropagation();
        console.log(tag, 'onTabDbClick', id);
        onDbClick(id);
    }, []);
    const onClose = useCallback(async (e, id) => {
        const res = await onBeforeClose([id]);
        if (id === null || id === void 0 ? void 0 : id.includes('&seq&')) {
            dispatch(MailActions.doRemWaittingMailId(id));
        }
        setDropdownVisible(false);
        if (res) {
            dispatch(mailTabActions.doCloseTab(id));
            dispatch(mailTabActions.doDeleteReadTabById(id));
        }
    }, []);
    const closeAll = useCallback(async () => {
        var _a;
        setDropdownVisible(false);
        if (tabList.length <= 1) {
            return;
        }
        const ids = (_a = tabList.filter(item => item.closeable)) === null || _a === void 0 ? void 0 : _a.map(item => item.id);
        const res = await onBeforeClose(ids);
        if (res) {
            dispatch(mailTabActions.doCleanTabs());
            dispatch(mailTabActions.doCleanReadTab());
            if (process.env.BUILD_ISEDM) {
                const customerTabs = tabList.filter(item => item.type && item.type === tabType.customer);
                customerTabs.length && dispatch(MailActions.doCleanTabs_cm(customerTabs));
            }
        }
        dispatch(MailActions.doSetWaittingMailIds([]));
    }, [tabList]);
    const renderItemTitle = (item) => {
        if (Array.isArray(item.subTabs) && item.subTabs.length) {
            const activeSubTab = item.subTabs.find(item => !!item.isActive);
            if (activeSubTab && ((activeSubTab === null || activeSubTab === void 0 ? void 0 : activeSubTab.type) == tabType.subordinate || (activeSubTab === null || activeSubTab === void 0 ? void 0 : activeSubTab.type) == tabType.customer)) {
                return htmlApi.decodeHtml(activeSubTab.proxyTitle || activeSubTab.title);
            }
            else {
                return htmlApi.decodeHtml(item.title);
            }
        }
        return htmlApi.decodeHtml(item.title);
    };
    const menuItem = useCallback((item) => (React.createElement(Menu.Item, { key: item.id, className: classnames(styles.tabMenuItem, { [styles.tabMenuItemDivider]: !item.closeable }) },
        React.createElement("span", { className: classnames(styles.tabMenuItemTxt, { blue: item.id === currentTabId }), onClick: e => {
                onSwitch(e, item.id);
            } }, renderItemTitle(item)),
        item.closeable ? (React.createElement("span", { onClick: e => {
                onClose(e, item.id);
            }, className: styles.tabMenuItemCloseIcon },
            React.createElement(TabMemuClose, null))) : (''))), [onSwitch, onClose]);
    const tabDropHandler = useCallback(dropId => {
        onDbClick(dropId);
    }, [onDbClick]);
    const handleDropdownDisplayChange = useCallback(isShow => {
        console.log(tag, 'handleDropdownDisplayChange', isShow);
        setDropdownButtonShow(isShow);
    }, [setDropdownButtonShow]);
    const renderTabBar = useCallback((props, DefaultTabBar) => (React.createElement(DefaultTabBar, Object.assign({}, props, { className: classnames(styles.tabs, [isMac ? styles.tabMac : styles.tabWindow], { [styles.backButtonPlace]: !inElectron() }, { [styles.hasAiAssistantIcon]: inElectron() && !process.env.BUILD_ISEDM && !aiAssistSideBarVisiable }) }), (node) => (React.createElement(TabNode, { key: node.key, id: node.key, index: node.key, isActive: activeKey === node.key, onDrop: tabDropHandler, onTabDbClick: onTabDbClick }, node)))), [activeKey, tabDropHandler, onTabDbClick, aiAssistSideBarVisiable]);
    const TabNode = useMemo(() => ({ id, children, onDrop, isActive, onTabDbClick }) => {
        const originalIndex = id;
        const [{ isDragging, didDrop }, drag, preview] = useDrag(() => ({
            type: ItemTypes.DND_TAB,
            options: {
                dropEffect: 'move',
            },
            item: () => {
                console.log(tag, 'beginDrag');
                return { id, originalIndex };
            },
            collect: monitor => ({
                isDragging: monitor.isDragging(),
                didDrop: monitor.didDrop(),
            }),
            end: (item, monitor) => {
                const { id: droppedId, originalIndex } = item;
                const didDrop = monitor.didDrop();
                console.log(tag, 'didDrop', !didDrop);
                if (!didDrop) {
                    onDrop(originalIndex);
                }
            },
        }), [id, originalIndex]);
        const [, drop] = useDrop(() => ({
            accept: ItemTypes.DND_TAB,
        }), []);
        const opacity = isDragging ? 0 : 1;
        const previewContent = (React.createElement("div", { ref: node => {
                if (id == '-1' || !inElectron()) {
                    return node;
                }
                return drag(drop(node));
            }, style: { opacity } }, children));
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { onDoubleClick: e => onTabDbClick(e, id), className: classnames(styles.tabNodeWrapper, { [styles.tabActive]: isActive }, { [styles.tabNodePin]: [tabId.readMain, tabId.readCustomer].includes(id) }, { [styles.tabNodeDragging]: isDragging }) }, previewContent)));
    }, []);
    const DropdownButton = useMemo(() => ({ dropdownVisible, displayChange, onClick = () => { }, isFloat }) => {
        const intersectionButton = useRef(null);
        const intersection = useRef(null);
        useEffect(() => {
            intersection.current = new IntersectionObserver(entries => {
                const entry = entries[0];
                console.log(tag, 'intersection', entry.intersectionRatio, entry.isIntersecting);
                if (!entry.isIntersecting) {
                    displayChange(false);
                }
                else {
                    displayChange(true);
                }
            });
            (intersectionButton === null || intersectionButton === void 0 ? void 0 : intersectionButton.current) && intersection.current.observe(intersectionButton === null || intersectionButton === void 0 ? void 0 : intersectionButton.current);
            return () => {
                (intersectionButton === null || intersectionButton === void 0 ? void 0 : intersectionButton.current) && intersection.current.unobserve(intersectionButton === null || intersectionButton === void 0 ? void 0 : intersectionButton.current);
                intersection.current.disconnect();
            };
        }, []);
        return (React.createElement("a", { ref: intersectionButton, onClick: onClick, className: classnames(styles.tabDropdownA, { [styles.tabDropdownActive]: dropdownVisible, [styles.tabDropdownAFloat]: isFloat }) }, inElectron() ? (React.createElement(IconCard, { height: "8px", stroke: dropdownVisible ? '#232D47' : '', width: "8px", type: "tabDropdown" })) : (React.createElement(IconCard, { height: "8px", stroke: dropdownVisible ? '#232D47' : '#8D92A1', width: "8px", type: "tabDropdown" }))));
    }, []);
    const dropDown = useCallback(() => (React.createElement(Dropdown, { overlay: React.createElement(Menu, { className: styles.tabMenu, hidden: !dropdownVisible },
            React.createElement(Menu.Item, { key: 'closeAll', hidden: (tabList === null || tabList === void 0 ? void 0 : tabList.length) <= 1, className: classnames(styles.tabMenuItem, styles.tabMenuItemDivider), onClick: () => {
                    closeAll();
                } },
                React.createElement("span", { className: styles.tabMenuItemCloseTxtRed }, getIn18Text('GUANBIQUANBU'))),
            React.createElement("div", { className: styles.tabMenuScrollWrap }, tabList.map(item => {
                return menuItem(item);
            }))), overlayClassName: styles.tabDropdownContainer, onVisibleChange: visible => setDropdownVisible(visible), trigger: ['click'], placement: "bottomCenter" },
        React.createElement(DropdownButton, { dropdownVisible: dropdownVisible, isFloat: isDropdownButtonFloat, displayChange: handleDropdownDisplayChange }))), [dropdownVisible, tabList, closeAll, isDropdownButtonFloat, handleDropdownDisplayChange]);
    const remove = async (targetKey) => {
        if (targetKey === null || targetKey === void 0 ? void 0 : targetKey.includes('&seq&')) {
            dispatch(MailActions.doRemWaittingMailId(targetKey));
        }
        const res = await onBeforeClose([targetKey]);
        if (res) {
            dispatch(mailTabActions.doCloseTab(targetKey));
            dispatch(mailTabActions.doDeleteReadTabById(targetKey));
            setTimeout(() => {
                const target = tabList.find(v => v.id === targetKey && [tabType.customer, tabType.subordinate].includes(v.type));
                if (target) {
                    if (target.type === tabType.customer) {
                        dispatch(MailActions.doRemoveSlice_cm({ sliceId: targetKey }));
                    }
                    else if (target.type === tabType.subordinate) {
                        dispatch(MailActions.doRemoveSlice_sd({ sliceId: targetKey }));
                    }
                }
            }, 1000);
        }
    };
    const onEdit = (targetKey, action) => {
        switch (action) {
            case 'remove':
                remove(targetKey);
                break;
            default:
                break;
        }
    };
    useEffect(() => {
        const newTabId = `${currentTabId}`;
        if (newTabId !== activeKey) {
            setTabActive(newTabId);
        }
    }, [currentTabId]);
    const tabChangeHandler = useCallback(id => {
        setTabActive(id);
        trackMailTabClick(id);
        dispatch(mailTabActions.doChangeCurrentTab(id));
    }, []);
    return (React.createElement(React.Fragment, null, (tabList.length && (React.createElement(DndProvider, { backend: HTML5Backend },
        React.createElement(Tabs, { size: "small", addIcon: dropDown(), className: classnames(process.env.BUILD_ISEDM ? styles.tabContainerEdm : styles.tabContainer, { [styles.floatDropdownWrap]: isDropdownButtonFloat }, { [styles.webComp]: !inElectron() && !process.env.BUILD_ISEDM }), onChange: tabChangeHandler, activeKey: activeKey, renderTabBar: renderTabBar, type: "editable-card", onEdit: onEdit, tabPosition: "top", animated: false }, tabList.map(pane => {
            if (pane) {
                return (React.createElement(TabPane, { closable: pane.closeable, closeIcon: React.createElement(TabCloseIcon, { className: inElectron() ? styles.tabCloseIcon : styles.tabCloseWebIcon }), tab: React.createElement(TabTitle, { data: pane, activeKey: activeKey, orginActiveKey: currentTabId }), key: pane.id }));
            }
            return null;
        }))))) ||
        ''));
};
export default MailTab;
